import { mutedSpeechDetectionStore } from './state/store/store';
import type { MutedSpeechDetector } from './mutedSpeechDetector';

// id контента меню-кнопок, с которыми тултип не должен пересекаться.
export const ObstacleIds = {
    ControlAudioMenu: 'control-audio-menu',
    ControlVideoMenu: 'control-video-menu',
    ControlReactions: 'control-reactions',
    ControlMoreMenu: 'control-more-menu',
} as const;

export type ObstacleId = (typeof ObstacleIds)[keyof typeof ObstacleIds];

// Небольшой запас, чтобы касание «край в край» не считалось перекрытием.
const OVERLAP_MARGIN = 2;

// Периоды опроса DOM.
// Проверяем наличие/геометрию меню state-based способом: просто спрашиваем DOM раз в интервал.
// Overlap-фаза (спрятать тултип при пересечении с ObstacleIds-элементами)
// чувствительна к задержке — опрашиваем часто.
// Close-фаза (возобновить детекцию после закрытия меню) к задержке
// нечувствительна, поэтому опрашиваем спокойнее.
const OVERLAP_POLL_INTERVAL_MS = 50;
const MENU_CLOSE_POLL_INTERVAL_MS = 200;

// Живой детектор — чтобы гард мог ставить детекцию на паузу и снимать.
let activeDetector: MutedSpeechDetector | null = null;

// Таймер опроса, работающий пока тултип виден: ждёт, что появятся пересечения с ObstacleIds-элементами.
let overlapPollId: ReturnType<typeof setInterval> | null = null;

// Таймер опроса, ждущий закрытия перекрывших ObstacleIds-элементами, чтобы снять детектор с паузы.
let menuClosePollId: ReturnType<typeof setInterval> | null = null;

function stopOverlapPoll() {
    if (overlapPollId !== null) clearInterval(overlapPollId);
    overlapPollId = null;
}

function stopMenuClosePoll() {
    if (menuClosePollId !== null) clearInterval(menuClosePollId);
    menuClosePollId = null;
}

// Возвращает те ObstacleIds, которые прямо сейчас геометрически перекрывают rect тултипа.
function getObstructingIds(rect: DOMRect, ids: string[]): string[] {
    const hits: string[] = [];
    for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const b = el.getBoundingClientRect();
        const clear =
            rect.right <= b.left + OVERLAP_MARGIN ||
            rect.left >= b.right - OVERLAP_MARGIN ||
            rect.bottom <= b.top + OVERLAP_MARGIN ||
            rect.top >= b.bottom - OVERLAP_MARGIN;
        if (!clear) hits.push(id);
    }
    return hits;
}

// Прячем тултип и уходим в паузу.
// Вызывается и при разовой проверке на маунте, и когда меню открылось поверх уже видимого тултипа.
function hideAndSuppress(obstructingIds: string[]) {
    stopOverlapPoll();
    mutedSpeechDetectionStore.setIsUserSpeakWhileMuted(false);
    suppressUntilMenusClose(obstructingIds);
}

// Пока тултип виден — раз в OVERLAP_POLL_INTERVAL_MS перемеряем тултип против ObstacleIds.
// Как только фиксируем пересечение — прячем тултип.
function watchMenusWhileVisible(node: HTMLElement) {
    stopOverlapPoll();

    const ids = Object.values(ObstacleIds);
    overlapPollId = setInterval(() => {
        const obstructingIds = getObstructingIds(node.getBoundingClientRect(), ids);
        if (obstructingIds.length === 0) return;
        hideAndSuppress(obstructingIds);
    }, OVERLAP_POLL_INTERVAL_MS);
}

// Ставим детекцию на паузу и раз в MENU_CLOSE_POLL_INTERVAL_MS проверяем, остались ли перекрывшие
// ObstacleIds-элементы в DOM. Как только их нет — снимаем паузу начисто, и детектор ловит речь заново, как в первый раз.
function suppressUntilMenusClose(obstructingIds: string[]) {
    stopMenuClosePoll();
    activeDetector?.pause();

    const stillObstructing = () => obstructingIds.some((id) => document.getElementById(id) !== null);

    menuClosePollId = setInterval(() => {
        if (stillObstructing()) return;
        stopMenuClosePoll();
        activeDetector?.resume();
    }, MENU_CLOSE_POLL_INTERVAL_MS);
}

// --- Публичное API для хука детекции ---

// Публикуем живой детектор, чтобы гард мог им управлять (pause/resume).
export function setActiveDetector(detector: MutedSpeechDetector) {
    activeDetector = detector;
}

// Снимаем детектор с публикации (по идентичности, чтобы не затереть уже пришедший на смену)
// и гасим close-опрос: паузу снимать больше не для кого.
export function releaseActiveDetector(detector: MutedSpeechDetector) {
    stopMenuClosePoll();
    if (activeDetector === detector) activeDetector = null;
}

// --- Публичное API для хука-гарда ---

// Маунт тултипа (node ≠ null): разовая проверка, не накрывает ли он уже открытые ObstacleIds-элементы. Если да —
// прячем тултип до отрисовки и ставим детекцию на паузу до закрытия меню. Если нет — тултип
// остаётся виден, и на всё время видимости запускаем опрос: если ObstacleIds-элементы пересекутся с тултипом
// позже, прячем тултип тем же путём.
// Анмаунт тултипа (node = null): гасим опрос открытия ObstacleIds-элементов.
export function handleTooltipNode(node: HTMLElement | null) {
    if (!node) {
        stopOverlapPoll();
        return;
    }

    const obstructingIds = getObstructingIds(node.getBoundingClientRect(), Object.values(ObstacleIds));
    if (obstructingIds.length > 0) {
        hideAndSuppress(obstructingIds);
        return;
    }

    watchMenusWhileVisible(node);
}
